import { useQuery } from "@tanstack/react-query";
// The manifest only changes when you run generate_manifest.py and redeploy —
// never during a session — so staleTime: Infinity means react-query fetches
// it once and every component sharing this query key reuses the same cached
// result, rather than each usePhoto()/usePhotosByOrientation() call
// triggering its own fetch.
function usePhotoManifest() {
    return useQuery({
        queryKey: ["photo-manifest"],
        queryFn: () => fetch("/dist/static/manifest.json").then((res) => res.json()),
        staleTime: Infinity,
    });
}
// Look up one specific photo by its source filename — the same way you'd
// have previously written a hardcoded src string.
//
//   const heroPhoto = usePhoto("KylieJeanPhotography-9328.jpg");
//   <img src={heroPhoto?.src} alt={heroPhoto?.alt} />
//
export function usePhoto(filename) {
    const { data: photos = [] } = usePhotoManifest();
    return photos.find((photo) => photo.source_filename === filename);
}
// Pull every photo of a given orientation — useful for building
// HeroPhotoSpread's photoSets from portrait/landscape slots automatically
// instead of hand-picking filenames per slot.
//
//   const portraits = usePhotosByOrientation("portrait");
//
export function usePhotosByOrientation(orientation) {
    const { data: photos = [] } = usePhotoManifest();
    return photos.filter((photo) => photo.orientation === orientation);
}
// Pull every photo tagged with a given group, sorted by that group's order
// value — this is what replaces a hardcoded array like
// `const portfolioPhotos = [...]`. Groups (and their per-group order) are
// authored by hand in photo-metadata.json (see generate_manifest.py), not
// inferred. Photos with no explicit order (null) sort after every photo
// that has one, in whatever order the manifest happened to list them.
//
//   const portfolioPhotos = usePhotosByGroup("portfolio");
//   const weddingPhotos = usePhotosByGroup("weddings"); // its own ordering
//
export function usePhotosByGroup(group) {
    const { data: photos = [] } = usePhotoManifest();
    return photos
        .filter((photo) => group in (photo.groups ?? {}))
        .slice() // avoid mutating react-query's cached array with .sort()
        .sort((a, b) => {
        const orderA = a.groups[group];
        const orderB = b.groups[group];
        if (orderA == null && orderB == null)
            return 0;
        if (orderA == null)
            return 1; // unordered sorts after ordered
        if (orderB == null)
            return -1;
        return orderA - orderB;
    });
}
// The full list, with loading/error state exposed — for anything that
// needs to render or filter across every photo (the main portfolio grid).
export function usePhotos() {
    const { data: photos = [], isLoading, error } = usePhotoManifest();
    return { photos, isLoading, error };
}
